import { useContext, useEffect, useState } from 'react';
import { Route, Switch, useHistory } from 'react-router';
import cx from 'classnames';

import { MDText } from 'i18n-react';

import { VehicleLookupContext } from 'Context/vehicleLookup';

import { EARLY_DOCS_COLLECTION, POST_SALE_DOCS_COLLECTION } from 'Utilities/routing';

import { ErrorPage } from '../../../../vehicleProfiling/ErrorPage/ErrorPage';

import { IncodeLoader } from './components/IncodeLoader';
import { IncodeSessionState } from './hooks/useIncodeVerificationSession';
import { IncodeProgressBar } from './incodeProgressBar/IncodeProgressBar';
import { usePostIncodeProgress } from './progress/incodeProgress';
import LocalTexts from './steps/Incode.texts.json';
import { IncodeSwitch } from './steps/IncodeSwitch';
import { INCODE_ANALYTICS } from './IncodeAnalytics';
import type { IncodeProgressStatus, IncodeSDK, IncodeStepProps } from './IncodeSDKHelpers';
import {
  API_URL, INCODE_BE_ROUTES, INCODE_PROGRESS_STATUS,
  INCODE_PROGRESS_STEP, INCODE_ROUTES, SENTRY_TAG,
  useIncodeSlug,
} from './IncodeSDKHelpers';
import {
  IncodeCaptureLicense,
  IncodeCaptureLicenseTutorial,
  IncodeCaptureProofOfAddress,
  IncodeCaptureProofOfAddressTutorial,
  IncodeCaptureSelfie,
  IncodeCaptureSelfieTutorial,
  IncodeCaptureV5c,
  IncodeMandatoryConsent,
  IncodeStart,
} from './steps';

import styles from './IncodeSdkPage.scss';

const LocalT = new MDText(LocalTexts.errorPage);

const DocCaptureWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.mwIncode}>{/* Can replace the div with <DocumentCaptureViewContainer> when ready */}
    {children}
  </div>
);

type IncodeSDKProps = {
  incodeSession: IncodeSessionState;
  renderCompleteComponent: (props: IncodeStepProps) => React.ReactNode;
  url: typeof POST_SALE_DOCS_COLLECTION | typeof EARLY_DOCS_COLLECTION;
};

export const IncodeSDKPage = ({ incodeSession, renderCompleteComponent, url }: IncodeSDKProps) => {
  const [hasError, setHasError] = useState(false);
  const [incodeSDKRef, setIncodeSDKRef] = useState<IncodeSDK | undefined>();
  const [nextStepLoaded, setNextStepLoaded] = useState(false);
  const { vehicleLookupState: vehicle } = useContext(VehicleLookupContext);

  const { postIncodeProgress } = usePostIncodeProgress();

  const history = useHistory();
  const slug = useIncodeSlug();

  const BASE_URL = `/${vehicle.vrm}${slug}`;
  const DP_ENQUIRY_ID = vehicle?.enquiry?.dpEnquiryId!;

  useEffect(() => {
    if (incodeSession.success === false) {
      setHasError(true);
    }
  }, [incodeSession.success]);

  useEffect(() => {
    const initIncode = async () => {
      const NOW = performance.now();

      const incodeModule = await import('@incodetech/welcome')
        .catch((error) => {
          window?.Sentry?.captureException(error, {
            tags: {
              action: 'load',
              ...SENTRY_TAG.tags,
            },
          });
          return {};
        });

      INCODE_ANALYTICS.INCODE_SDK_LOADED({
        loadEventEnd: performance.now() - NOW,
        name: 'incode_sdk_loaded',
      });

      const { create } = incodeModule as IncodeSDK;

      if (!create) {
        setHasError(true);
        return;
      }

      setIncodeSDKRef(create({
        apiURL: API_URL,
        lang: 'en',
        translations: {
          notifications: {
            showUtilityBill: {
              one: 'Take a photo of your V5C document',
              three: '',
              two: '',
            },
          },
        },
      }));
    };

    initIncode();
  }, []);

  useEffect(() => {
    const { session } = incodeSession;

    if (!incodeSDKRef || !session?.token) {
      return () => {};
    }

    const NOW = performance.now();

    const step = { step: INCODE_PROGRESS_STEP.FINGERPRINT };
    const abortController = new AbortController();

    incodeSDKRef.sendFingerprint({ token: session?.token })
      .then(async () => {
        const { allStepsCompleted, nextStep }: {
          allStepsCompleted: boolean;
          nextStep?: IncodeProgressStatus['step'];
        } = await postIncodeProgress(
          session,
          { status: INCODE_PROGRESS_STATUS.SUCCESS, ...step },
          abortController,
        );

        if (allStepsCompleted) {
          history.replace(`${BASE_URL}${INCODE_ROUTES.COMPLETE}`);
        }

        if (nextStep) {
          history.replace(`${BASE_URL}${INCODE_BE_ROUTES[nextStep]}`);
        }
      })
      .catch((e: Error) => {
        postIncodeProgress(session, { status: INCODE_PROGRESS_STATUS.FAILED, ...step });
        window?.Sentry?.captureException?.(e, SENTRY_TAG);
      })
      .finally(() => {
        setNextStepLoaded(true);

        INCODE_ANALYTICS.INCODE_SDK_LOADED({
          loadEventEnd: performance.now() - NOW,
          name: 'incode_session_loaded',
        });

        INCODE_ANALYTICS.INCODE_SECTION_LOADED('incode_ready');
      });

    return () => {
      abortController.abort();
    };
  }, [BASE_URL, history, incodeSDKRef, incodeSession, postIncodeProgress]);

  useEffect(() => {
    if (hasError) {
      window?.Sentry?.captureException(new Error('Incode SDK - Error state reached'), SENTRY_TAG);
    }
  }, [hasError]);

  useEffect(() => INCODE_ANALYTICS.INCODE_REMOVE_CONTEXT, []);

  if (hasError) {
    return (
      <div className={styles.mwIncodeError}>
        <ErrorPage
          questionSubtitle={LocalT.translate('questionSubtitle')}
        />
      </div>
    );
  }

  if (!incodeSDKRef || !incodeSession.success || !nextStepLoaded) {
    return (
      <div className={cx(styles.mwIncode, styles.pageLoader)}>
        <IncodeLoader />
      </div>
    );
  }

  const PROPS: IncodeStepProps = {
    BASE_URL,
    DP_ENQUIRY_ID,
    incodeSDK: incodeSDKRef,
    incodeSession: incodeSession.session,
  };

  const routes = [
    [`${url}${INCODE_ROUTES.SELFIE}`, IncodeCaptureSelfie, IncodeCaptureSelfieTutorial],
    [`${url}${INCODE_ROUTES.V5C}`, IncodeCaptureV5c, IncodeCaptureV5c],
    [`${url}${INCODE_ROUTES.CAPTURE_LICENSE}`, IncodeCaptureLicense, IncodeCaptureLicenseTutorial],
    [`${url}${INCODE_ROUTES.PROOF_OF_ADDRESS}`, IncodeCaptureProofOfAddress, IncodeCaptureProofOfAddressTutorial],
    [`${url}`, IncodeMandatoryConsent, IncodeStart],
  ] as const;

  return (
    <DocCaptureWrapper>
      <IncodeProgressBar />
      <Switch>
        <Route path={`${url}${INCODE_ROUTES.COMPLETE}`} render={() => renderCompleteComponent(PROPS)} />
        {
          routes.map(([path, IncodeComponent, IncodeTutorial]) => (
            <Route
              key={path}
              path={path}
              render={() => <IncodeSwitch incode={IncodeComponent} splash={IncodeTutorial} {...PROPS} />}
            />
          ))
        }
      </Switch>
    </DocCaptureWrapper>
  );
};
